import { useEffect, useRef, useState } from "react";
import {
  Check,
  Copy,
  ExternalLink,
  ImagePlus,
  Loader2,
  Lock,
  RefreshCw,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const ACCEPTED_LOGO_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
] as const;

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

type AcceptedLogoMime = (typeof ACCEPTED_LOGO_MIME_TYPES)[number];

function isAcceptedLogoMime(mime: string): mime is AcceptedLogoMime {
  return (ACCEPTED_LOGO_MIME_TYPES as readonly string[]).includes(mime);
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      resolve(result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}

interface CityReportShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  reportToken: string;
  reportEnabled: boolean;
  reportClosedAt: string | null;
  hubSize: number | null;
  title: string | null;
  clientName: string | null;
  intro: string | null;
  logoUrl: string | null;
}

export function CityReportShareDialog({
  open,
  onOpenChange,
  collectionId,
  reportToken,
  reportEnabled,
  reportClosedAt,
  hubSize,
  title,
  clientName,
  intro,
  logoUrl,
}: CityReportShareDialogProps) {
  const utils = trpc.useUtils();
  const [copied, setCopied] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDragActive, setLogoDragActive] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const [titleVal, setTitleVal] = useState(title ?? "");
  const [clientVal, setClientVal] = useState(clientName ?? "");
  const [introVal, setIntroVal] = useState(intro ?? "");
  const [logoVal, setLogoVal] = useState(logoUrl ?? "");
  const [hubVal, setHubVal] = useState<string>(
    hubSize != null ? String(hubSize) : "",
  );

  // Keep local state in sync if props change (e.g., after invalidation)
  useEffect(() => {
    setTitleVal(title ?? "");
  }, [title]);
  useEffect(() => {
    setClientVal(clientName ?? "");
  }, [clientName]);
  useEffect(() => {
    setIntroVal(intro ?? "");
  }, [intro]);
  useEffect(() => {
    setLogoVal(logoUrl ?? "");
  }, [logoUrl]);
  useEffect(() => {
    setHubVal(hubSize != null ? String(hubSize) : "");
  }, [hubSize]);

  const shareUrl = `${window.location.origin}/city-review/${reportToken}`;
  const closed = !!reportClosedAt;

  const invalidate = () =>
    Promise.all([
      utils.physicalMapping.getCityReviewSummary.invalidate({ collectionId }),
      utils.collections.getById.invalidate({ id: collectionId }),
      utils.collections.list.invalidate(),
    ]);

  const enableMut = trpc.physicalMapping.enableReport.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("City report enabled");
    },
    onError: (e) => toast.error(e.message),
  });

  const disableMut = trpc.physicalMapping.disableReport.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("City report disabled");
    },
    onError: (e) => toast.error(e.message),
  });

  const updateSettings = trpc.physicalMapping.updateReportSettings.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const uploadLogo = trpc.physicalMapping.uploadReportLogo.useMutation();

  const setHub = trpc.physicalMapping.setHubSize.useMutation({
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const regenerate = trpc.physicalMapping.regenerateReportToken.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Link regenerated");
      setRegenerateOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const closeReport = trpc.physicalMapping.closeReport.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Report closed");
    },
    onError: (e) => toast.error(e.message),
  });

  const reopenReport = trpc.physicalMapping.reopenReport.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Report reopened");
    },
    onError: (e) => toast.error(e.message),
  });

  async function handleCopy() {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setCopied(false), 2000);
  }

  const handleToggle = (next: boolean) => {
    if (next) enableMut.mutate({ collectionId });
    else disableMut.mutate({ collectionId });
  };

  const handleLogoFile = async (file: File) => {
    if (!isAcceptedLogoMime(file.type)) {
      toast.error("Logo must be a PNG, JPG, SVG, or WebP image");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo must be 2MB or smaller");
      return;
    }
    setLogoUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const result = await uploadLogo.mutateAsync({
        collectionId,
        base64,
        mimeType: file.type as AcceptedLogoMime,
      });
      await updateSettings.mutateAsync({
        collectionId,
        logoUrl: result.url,
      });
      setLogoVal(result.url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setLogoUploading(true);
    try {
      await updateSettings.mutateAsync({
        collectionId,
        logoUrl: null,
      });
      setLogoVal("");
      toast.success("Logo removed");
    } catch {
      // handled in onError
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveBranding = async () => {
    const parsedHub =
      hubVal.trim() === ""
        ? null
        : Math.max(1, Math.trunc(Number(hubVal)));
    if (hubVal.trim() !== "" && !Number.isFinite(parsedHub)) {
      toast.error("Hub size must be a positive number");
      return;
    }
    try {
      await Promise.all([
        updateSettings.mutateAsync({
          collectionId,
          title: titleVal.trim() || null,
          clientName: clientVal.trim() || null,
          intro: introVal.trim() || null,
        }),
        setHub.mutateAsync({ collectionId, size: parsedHub }),
      ]);
      toast.success("Branding saved");
    } catch {
      // Handled in onError
    }
  };

  const busyToggle = enableMut.isPending || disableMut.isPending;
  const busySave = updateSettings.isPending || setHub.isPending;
  const busyClose = closeReport.isPending || reopenReport.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Share with city</DialogTitle>
            <DialogDescription>
              Generate a read-only review link for the city to validate stations.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Enable report</Label>
                <p className="text-xs text-muted-foreground">
                  Allows anyone with the link to review main & backup stations.
                </p>
              </div>
              <Switch
                checked={reportEnabled}
                onCheckedChange={handleToggle}
                disabled={busyToggle}
              />
            </div>

            {closed && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <Lock className="size-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium">Report is closed</div>
                  <p className="text-xs text-amber-800">
                    City can still view the link but cannot submit new verdicts
                    or comments.
                  </p>
                </div>
              </div>
            )}

            {reportEnabled && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Report URL
                </Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={shareUrl}
                    className="text-xs h-9"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <Button size="sm" className="h-9 shrink-0" onClick={handleCopy}>
                    {copied ? (
                      <Check className="size-3.5" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 shrink-0"
                    asChild
                  >
                    <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="size-3.5" />
                    </a>
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-3 rounded-lg border p-3">
              <div>
                <Label className="text-sm font-medium">Branding</Label>
                <p className="text-xs text-muted-foreground">
                  Customize the report the city sees.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="report-title" className="text-xs">
                  Report title
                </Label>
                <Input
                  id="report-title"
                  value={titleVal}
                  onChange={(e) => setTitleVal(e.target.value)}
                  placeholder="Proposed station locations"
                  maxLength={200}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="report-client" className="text-xs">
                  Client / City name
                </Label>
                <Input
                  id="report-client"
                  value={clientVal}
                  onChange={(e) => setClientVal(e.target.value)}
                  placeholder="City of Springfield"
                  maxLength={200}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="report-hub" className="text-xs">
                  Hub size
                </Label>
                <Input
                  id="report-hub"
                  type="number"
                  min={1}
                  step={1}
                  value={hubVal}
                  onChange={(e) => setHubVal(e.target.value)}
                  placeholder="e.g. 10"
                />
                <p className="text-[11px] text-muted-foreground">
                  Target number of main stations in this hub.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="report-intro" className="text-xs">
                  Intro
                </Label>
                <Textarea
                  id="report-intro"
                  value={introVal}
                  onChange={(e) => setIntroVal(e.target.value)}
                  placeholder="Add context for the reviewer…"
                  rows={4}
                  maxLength={5000}
                />
                <p className="text-[11px] text-muted-foreground">
                  Markdown supported.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Logo</Label>
                {logoVal ? (
                  <div className="flex items-center gap-3 rounded-md border p-2">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
                      <img
                        src={logoVal}
                        alt="Report logo"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs text-muted-foreground">
                        {logoVal}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Shown in the public report header.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                      onClick={handleRemoveLogo}
                      disabled={logoUploading}
                    >
                      <Trash2 className="size-3.5 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLogoDragActive(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLogoDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLogoDragActive(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setLogoDragActive(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) void handleLogoFile(file);
                    }}
                    disabled={logoUploading}
                    className={cn(
                      "flex w-full min-h-24 flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed bg-muted/20 px-3 py-4 text-muted-foreground transition-colors hover:bg-muted/40 disabled:opacity-50",
                      logoDragActive
                        ? "border-primary bg-primary/5"
                        : "border-muted-foreground/40",
                    )}
                    aria-label="Upload report logo"
                  >
                    {logoUploading ? (
                      <>
                        <Loader2 className="size-5 animate-spin" />
                        <span className="text-xs font-medium">
                          Uploading…
                        </span>
                      </>
                    ) : (
                      <>
                        <ImagePlus className="size-5" />
                        <span className="text-xs font-medium">
                          Drop a logo here, or click to upload
                        </span>
                        <span className="text-[11px]">
                          PNG, JPG, SVG, or WebP · up to 2MB
                        </span>
                      </>
                    )}
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleLogoFile(file);
                    e.target.value = "";
                  }}
                />
              </div>

              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={handleSaveBranding}
                  disabled={busySave}
                >
                  {busySave ? "Saving…" : "Save branding"}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRegenerateOpen(true)}
                disabled={regenerate.isPending}
              >
                <RefreshCw className="size-3.5 mr-1.5" />
                Regenerate link
              </Button>
              {closed ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reopenReport.mutate({ collectionId })}
                  disabled={busyClose}
                >
                  Reopen report
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                  onClick={() => closeReport.mutate({ collectionId })}
                  disabled={busyClose}
                >
                  <Lock className="size-3.5 mr-1.5" />
                  Close report
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="size-4 text-amber-600" />
              Regenerate share link?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The existing link will stop working immediately. Anyone who has the
              current link will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerate.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={regenerate.isPending}
              onClick={(e) => {
                e.preventDefault();
                regenerate.mutate({ collectionId });
              }}
            >
              {regenerate.isPending ? "Regenerating…" : "Regenerate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
